import { useSearch } from "@tanstack/react-router";

import { cn } from "@/lib/utils";

import type { Page } from "../../api";
import { Chip } from "../../chips";
import { readShared } from "../../filters";
import { TabPage } from "../../PageFrame";
import { Legend, Swatch } from "../bars";
import type { PageProps } from "../index";
import { PageHead } from "../kit";
import { ALL_ROWS } from "../params";

export interface GeoRow {
  level: "country" | "region" | "untagged";
  geo_value: string;
  country: string | null;
  options_n: number;
  untagged_n: number;
  options_total: number;
  is_untagged: boolean;
}

/** Grid position (column, row) of each country tile, as drawn in mockup v6. */
export const TILES: Record<string, readonly [number, number]> = {
  NO: [5, 0], SE: [6, 0], FI: [7, 0], IE: [0, 1], UK: [1, 1], DK: [5, 1], EE: [8, 1], NL: [3, 2], DE: [4, 2],
  PL: [5, 2], LV: [8, 2], BE: [3, 3], CZ: [4, 3], SK: [5, 3], LT: [8, 3], FR: [2, 4], AT: [4, 4], HU: [5, 4],
  PT: [0, 5], ES: [1, 5], IT: [4, 5], SI: [5, 5], HR: [6, 5], QA: [10, 4], SA: [9, 5], AE: [10, 5],
};
/** Tile size and grid step in px. */
export const TILE_STEP = 58;
export const MAP_WIDTH = 11 * TILE_STEP;
export const MAP_HEIGHT = 6 * TILE_STEP + 10;
/** Regions that always show a chip, even at 0. */
export const REGIONS: readonly string[] = ["EU", "GCC", "UK"];

/** 0, 1, 2 to 3, 4 or more options. */
export const shade = (count: number): 0 | 1 | 2 | 3 => (count <= 0 ? 0 : count === 1 ? 1 : count <= 3 ? 2 : 3);

export const SHADE_CLASS: readonly string[] = [
  "border-ks-grey-3 bg-ks-surface text-ks-grey-1",
  "border-ks-purple-2 bg-ks-purple-2 text-ks-ink",
  "border-ks-purple-1 bg-ks-purple-1 text-ks-surface",
  "border-ks-purple bg-ks-purple text-ks-surface",
];

export interface GeoModel {
  countries: Record<string, number>;
  regions: Array<{ name: string; count: number }>;
  /** Countries with options but no tile on the map. */
  offMap: Array<{ code: string; count: number }>;
  untagged: number;
  total: number;
}

/** Rows are keyed on level + value: UK is both a country and a region, and the two are never added together. */
export function geoModel(rows: GeoRow[]): GeoModel {
  const countries: Record<string, number> = {};
  const regionCounts: Record<string, number> = {};
  for (const row of rows) {
    if (row.level === "country") countries[row.geo_value] = row.options_n;
    else if (row.level === "region") regionCounts[row.geo_value] = row.options_n;
  }
  const extraRegions = Object.keys(regionCounts).filter((name) => !REGIONS.includes(name)).sort();
  return {
    countries,
    regions: [...REGIONS, ...extraRegions].map((name) => ({ name, count: regionCounts[name] ?? 0 })),
    offMap: Object.keys(countries)
      .filter((code) => !(code in TILES))
      .sort()
      .map((code) => ({ code, count: countries[code] })),
    untagged: rows[0]?.untagged_n ?? 0,
    total: rows[0]?.options_total ?? 0,
  };
}

export function GeoSegmentationBody({ rows, selected }: { rows: GeoRow[]; selected: string | null }) {
  const model = geoModel(rows);
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {model.regions.map((region) => (
          <Chip key={region.name}>
            {region.name}: <b>{region.count}</b>
          </Chip>
        ))}
        {model.offMap.map((country) => (
          <Chip key={country.code} title="Not drawn on the map">
            {country.code}: <b>{country.count}</b>
          </Chip>
        ))}
        <Chip tone="warn" title="Options with no country or region tag">
          Untagged: {model.untagged} of {model.total}
        </Chip>
      </div>
      <div className="overflow-x-auto">
        <div className="relative" style={{ width: MAP_WIDTH, height: MAP_HEIGHT }}>
          {Object.entries(TILES).map(([code, [column, row]]) => {
            const count = model.countries[code] ?? 0;
            return (
              <div
                key={code}
                title={`${code}: ${count} options`}
                className={cn(
                  "absolute flex size-[52px] flex-col items-center justify-center rounded-[6px] border text-xs",
                  SHADE_CLASS[shade(count)],
                  selected === code && "outline-[3px] outline-ks-warn",
                )}
                style={{ left: column * TILE_STEP, top: row * TILE_STEP }}
              >
                <span>{code}</span>
                <b className="text-base">{count}</b>
              </div>
            );
          })}
        </div>
      </div>
      <Legend>
        <Swatch className="border border-ks-grey-3">0</Swatch>
        <Swatch className="bg-ks-purple-2">1</Swatch>
        <Swatch className="bg-ks-purple-1">2–3</Swatch>
        <Swatch className="bg-ks-purple">4+</Swatch>
      </Legend>
    </>
  );
}

export function GeoSegmentationPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const selected = readShared(search).geo;
  // The map shows every country at once, so the Geo filter picks a tile to highlight and is not sent to C1.
  const mapTab = { ...tab, accepts: tab.accepts.filter((name) => name !== "geo") };
  return (
    <TabPage<Page<GeoRow>>
      tab={mapTab}
      areaName={areaName}
      extra={{ limit: ALL_ROWS }}
      isEmpty={(data) => data.items.length === 0}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data ? "Options per country or region · darker = more options" : undefined}
          kpi={data ? { label: "Untagged", value: geoModel(data.items).untagged.toLocaleString("en-GB") } : null}
        />
      )}
    >
      {(data) => <GeoSegmentationBody rows={data.items} selected={selected} />}
    </TabPage>
  );
}
